import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Button, Chip, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useMemo, useState, type MouseEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useStorage } from '../../hooks/storage/useStorage';
import { RAPID_ROBOT_COMPETITIONS, RAPID_ROBOT_COMPLEXITY, RAPID_ROBOTS, type RapidRobotComplexity } from '../../utils/constants';
import HelpPopover from '../HelpPopover';
import RapidRobotCard from './RapidRobotCard';

const RAPID_ROBOTS_PAGE_STORAGE_KEY = 'rapidRobots.page';

type RapidRobotsPagePreferences = {
  noticeDismissed: boolean;
};

const DEFAULT_PAGE_PREFERENCES: RapidRobotsPagePreferences = {
  noticeDismissed: false,
};

const RapidRobotsCarousel = () => {
  const { t } = useTranslation();
  const [pagePreferences, setPagePreferences] = useStorage('local', RAPID_ROBOTS_PAGE_STORAGE_KEY, DEFAULT_PAGE_PREFERENCES);
  // Complexity intentionally resets to Motors on every visit rather than persisting.
  const [complexity, setComplexity] = useState<RapidRobotComplexity>(RAPID_ROBOT_COMPLEXITY.Motors);
  const showNotice = !pagePreferences.noticeDismissed;
  const isAdvanced = complexity === RAPID_ROBOT_COMPLEXITY.MotorsAndSensors;

  const robotsByKey = useMemo(() => {
    const robotsForComplexity = RAPID_ROBOTS.filter((robot) => robot.complexity === complexity);
    return new Map(robotsForComplexity.map((robot) => [robot.key, robot]));
  }, [complexity]);

  const handleComplexityChange = (_event: MouseEvent<HTMLElement>, nextComplexity: RapidRobotComplexity | null) => {
    if (nextComplexity == null) return;
    setComplexity(nextComplexity);
  };

  return (
    <Stack spacing={{ xs: 4, lg: 5 }} sx={{ width: '100%', maxWidth: 1440, mx: 'auto', pb: 4 }}>
      {showNotice ? (
        <Box
          role="status"
          sx={{
            borderRadius: '6px',
            backgroundColor: 'rapidRobot.noticeBackground',
            color: 'rapidRobot.noticeText',
            px: 1.5,
            py: 0.75,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <HelpPopover
              id="rapid-robots-page-notice-popover"
              ariaLabel={t('rapidRobots.page.noticeInfoAriaLabel', { defaultValue: 'More information about Rapid Robots' })}
              text={t('rapidRobots.page.noticeInfo', {
                defaultValue: 'Rapid Robot projects start from ready-made robot templates that you can customize for each competition.',
              })}
            />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 700, lineHeight: 1.35 }}>
              <Trans i18nKey="rapidRobots.carousel.title">Use these projects to customize your Rapid Robot</Trans>
            </Typography>
            <IconButton
              size="small"
              aria-label={t('rapidRobots.page.dismissNoticeAriaLabel', { defaultValue: 'Dismiss Rapid Robots information' })}
              onClick={() => setPagePreferences({ ...pagePreferences, noticeDismissed: true })}
              sx={{ color: 'inherit' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      ) : null}

      {RAPID_ROBOT_COMPETITIONS.map((competition) => {
        const robots = competition.robotKeys.map((robotKey) => robotsByKey.get(robotKey)).filter((robot) => robot !== undefined);

        return (
          <Stack
            key={competition.key}
            component="section"
            spacing={3}
            sx={{
              borderTop: competition === RAPID_ROBOT_COMPETITIONS[0] ? 'none' : 1,
              borderColor: 'divider',
              pt: competition === RAPID_ROBOT_COMPETITIONS[0] ? 0 : 4,
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    {t(competition.titleKey, competition.title)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700 }}>
                      <Trans i18nKey="rapidRobots.competition.compatibleWith">Compatible with:</Trans>
                    </Typography>
                    {robots.map((robot) => (
                      <Chip
                        key={robot.code}
                        label={t(robot.nameKey, robot.name)}
                        size="small"
                        sx={{
                          backgroundColor: `rapidRobot.${robot.accent}`,
                          color: 'rapidRobot.pillText',
                          borderRadius: '999px',
                          fontWeight: 700,
                          '& .MuiChip-label': {
                            px: 1.25,
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                  {t(competition.descriptionKey, competition.description)}
                </Typography>
              </Stack>

              <Stack spacing={1} sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                <Button
                  component="a"
                  href={competition.gameRulesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  endIcon={<ChevronRightIcon />}
                  aria-label={t('rapidRobots.competition.gameRulesAriaLabel', {
                    competition: t(competition.titleKey, competition.title),
                    defaultValue: '{{competition}} game rules',
                  })}
                  sx={(theme) => ({
                    flexShrink: 0,
                    '&&': {
                      backgroundColor: 'accent.main',
                      color: 'spheroWhite.main',
                      minHeight: 34,
                      px: 2,
                      py: 0.5,
                      borderRadius: '6px',
                      boxShadow: theme.shadows[2],
                      '& .MuiTypography-root': {
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                      },
                      '& .MuiButton-endIcon': {
                        ml: 0.75,
                      },
                      '&:hover': {
                        backgroundColor: 'accent.hover',
                        boxShadow: theme.shadows[3],
                      },
                    },
                  })}
                >
                  <Trans i18nKey="rapidRobots.competition.gameRules">Game Rules</Trans>
                </Button>
                <Button
                  component="a"
                  href={competition.lessonsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  endIcon={<ChevronRightIcon />}
                  aria-label={t('rapidRobots.competition.lessonsAriaLabel', {
                    competition: t(competition.titleKey, competition.title),
                    defaultValue: '{{competition}} lessons',
                  })}
                  sx={(theme) => ({
                    flexShrink: 0,
                    '&&': {
                      backgroundColor: 'accent.main',
                      color: 'spheroWhite.main',
                      minHeight: 34,
                      px: 2,
                      py: 0.5,
                      borderRadius: '6px',
                      boxShadow: theme.shadows[2],
                      '& .MuiTypography-root': {
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                      },
                      '& .MuiButton-endIcon': {
                        ml: 0.75,
                      },
                      '&:hover': {
                        backgroundColor: 'accent.hover',
                        boxShadow: theme.shadows[3],
                      },
                    },
                  })}
                >
                  <Trans i18nKey="rapidRobots.competition.lessons">Lessons</Trans>
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={1.5}>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    <Trans i18nKey="rapidRobots.complexity.label">Complexity:</Trans>
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={complexity}
                    onChange={handleComplexityChange}
                    aria-label={t('rapidRobots.complexity.ariaLabel', {
                      competition: t(competition.titleKey, competition.title),
                      defaultValue: 'Select {{competition}} Rapid Robot complexity',
                    })}
                    sx={{
                      borderRadius: 999,
                      overflow: 'hidden',
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: 'background.default',
                      '& .MuiToggleButtonGroup-grouped': {
                        border: 0,
                        borderRadius: 0,
                        minHeight: 28,
                        px: 1.25,
                        py: 0.375,
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        lineHeight: 1,
                        letterSpacing: 0,
                        textTransform: 'none',
                        '&:not(:first-of-type)': {
                          borderLeft: 1,
                          borderColor: 'divider',
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        '&.Mui-selected': {
                          color: 'text.primary',
                          backgroundColor: 'background.paper',
                          fontWeight: 600,
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'background.paper',
                        },
                      },
                    }}
                  >
                    <ToggleButton value={RAPID_ROBOT_COMPLEXITY.Motors}>
                      {t('rapidRobots.complexity.option.motors', { defaultValue: 'Motors' })}
                    </ToggleButton>
                    <ToggleButton value={RAPID_ROBOT_COMPLEXITY.MotorsAndSensors}>
                      {t('rapidRobots.complexity.option.motorsAndSensors', { defaultValue: 'Motors & Sensors' })}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {isAdvanced
                    ? t('rapidRobots.complexity.description.motorsAndSensors', {
                        defaultValue: 'Advanced Rapid Robot variants with sensor-enhanced mechanisms.',
                      })
                    : t('rapidRobots.complexity.description.motors', {
                        defaultValue: 'Standard Rapid Robot variants focused on motor-driven mechanisms.',
                      })}
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                  },
                  gap: { xs: 2, md: 3 },
                  alignItems: 'stretch',
                }}
              >
                {robots.map((robot) => (
                  <RapidRobotCard
                    key={robot.code}
                    code={robot.code}
                    nameKey={robot.nameKey}
                    name={robot.name}
                    subtitleKey={robot.subtitleKey}
                    subtitle={robot.subtitle}
                    description={robot.description}
                    descriptionKey={robot.descriptionKey}
                    electronics={robot.electronics}
                    image={robot.image}
                    accent={robot.accent}
                    complexity={robot.complexity}
                  />
                ))}
              </Box>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default RapidRobotsCarousel;
